import React, { useState, useRef, useEffect } from "react";
import "./styles/CustomerSupportChatbot.css";

const CustomerSupportChatbot = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([
    {
      type: "bot",
      content:
        "Hi! I'm your AI assistant. I can help you find the perfect products for your needs. What are you looking for today?",
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  // Company information - customize this with your actual company data
  const COMPANY_INFO = `
    Company: Devanshi Corporation
    Products: 
    - Potassium Bicarbonate Technical Grade
    - Potassium Carbonate Industrial Grade
    - Ammonium Molybdate Powder
    - Magnesium Oxide Powder
    - Sodium Molybdate Powder
    - Ammonium Chloride Technical Grade
    - Phosphorous Acid
    - Potassium Humate Flakes
    - Potassium Humate Powder
    - Sodium Humate Flakes
    - Potassium Fulvate
    
    Contact Information:
    - Phone: +91-9920850545
    - WhatsApp: Available via the floating button
    - Email: devanshicorporation@gmail.com
    - Address: 215 Reena Complex, opp Neelkanth Corporate Park, Vidyavihar West, Mumbai, Maharashtra 400086
    
    Special Details:
    - Immediate shipping
    - 24/7 Support
    - Quality tested products
  `;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const detectOrderIntent = (message) => {
    const orderKeywords = [
      "buy",
      "purchase",
      "order",
      "get",
      "want to buy",
      "interested in buying",
      "how much",
      "price",
      "cost",
      "checkout",
      "payment",
    ];
    return orderKeywords.some((keyword) =>
      message.toLowerCase().includes(keyword)
    );
  };

  const sendMessage = async () => {
    if (!inputValue.trim()) return;

    const userMessage = {
      type: "user",
      content: inputValue,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);

    try {
      // Check if user is showing order intent
      const hasOrderIntent = detectOrderIntent(inputValue);

      const systemPrompt = `You are a helpful customer support and sales AI agent for chemical wholesaler company - Devanshi Corporation. Use this company information to assist customers:
      
      ${COMPANY_INFO}
      
      Guidelines:
      1. Be friendly and professional
      2. Help customers find products that match their needs
      3. Provide specific product recommendations with prices
      4. If the customer shows interest in buying/ordering (uses words like "buy", "purchase", "order", "get", "want to buy", "interested in buying"), redirect them to contact information for placing orders
      5. Mention special offers when relevant
      6. Keep responses concise but helpful
      7. If you don't have specific information, be honest about it
      
      ${
        hasOrderIntent
          ? "IMPORTANT: The customer is showing interest in making a purchase. After providing product info, redirect them to contact information to complete their order."
          : ""
      }
      `;

      console.log("env", process.env.REACT_APP_GROQ_API_KEY);
      const response = await fetch(
        "https://api.groq.com/openai/v1/chat/completions",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${process.env.REACT_APP_GROQ_API_KEY}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            model: "llama3-8b-8192", // or 'llama2-70b-4096'
            messages: [
              { role: "system", content: systemPrompt },
              { role: "user", content: inputValue },
            ],
            max_tokens: 1000,
            temperature: 0.7,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("API request failed");
      }

      const data = await response.json();
      const botResponse = data.choices[0].message.content;

      let finalResponse = botResponse;

      // Add contact redirection for order intent
      if (hasOrderIntent) {
        finalResponse += `\n\n🛒 Ready to place your order? Please contact us:\n📞 Call: +91-9920850545\n💬 WhatsApp: Click the green button\n📧 Email: devanshicorporation@gmail.com\n\nOur team will help you complete your purchase!`;
      }

      const botMessage = {
        type: "bot",
        content: finalResponse,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      console.error("Error:", error);
      const errorMessage = {
        type: "bot",
        content:
          "I apologize, but I'm having trouble connecting right now. Please contact our support team directly at +91-9920850545 or use the WhatsApp button for immediate assistance.",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="chatbot-overlay">
      <div className="chatbot-container">
        <div className="chatbot-header">
          <div className="chatbot-title">
            <i className="fa-solid fa-robot"></i>
            <span>AI Customer Support</span>
          </div>
          <button className="chatbot-close" onClick={onClose}>
            <i className="fa-solid fa-times"></i>
          </button>
        </div>

        <div className="chatbot-messages">
          {messages.map((message, index) => (
            <div key={index} className={`message ${message.type}-message`}>
              <div className="message-content">
                {message.content.split("\n").map((line, i) => (
                  <div key={i}>{line}</div>
                ))}
              </div>
              <div className="message-time">
                {message.timestamp.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="message bot-message">
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        <div className="chatbot-input">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your message..."
            disabled={isLoading}
          />
          <button
            onClick={sendMessage}
            disabled={isLoading || !inputValue.trim()}>
            <i className="fas fa-paper-plane"></i>
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomerSupportChatbot;
